// src/contexts/ProductsContext.js
import React, { createContext, useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export const ProductsContext = createContext({
  products: [],
  imageItems: [],
  getCoverImage: () => null,
  loading: true,
});

export function ProductsProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [imageItems, setImageItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Keep already-open tabs synchronized through the expand migration. A
  // one-shot read could leave a customer browsing legacy prices after the
  // canonical unitAmount fields are written.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'products'),
      snap => {
        setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      error => {
        console.error('Error fetching products:', error);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  // Real-time listener for all imageItems
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'imageItems'), snap => {
      const allImages = snap.docs.map(d => {
        const x = d.data();
        return {
          id: d.id,
          caption: x.caption ?? '',
          imageURL: x.imageURL ?? '',
          thumbnailURL: x.thumbnailURL ?? '',
          productID: x.productID ?? null,
        };
      });
      setImageItems(allImages);
    });
    return () => unsub();
  }, []);

  // Helper: find cover image for a given product (similar to iOS ImageItemsManager)
  const getCoverImage = product => {
    if (!product) return null;

    // First try to find by productImage.imageItemID
    if (product.productImage?.imageItemID) {
      const coverImage = imageItems.find(img => img.id === product.productImage.imageItemID);
      if (coverImage) return coverImage;
    }

    // Fall back to finding by productID
    return imageItems.find(img => img.productID === product.id) || null;
  };

  const value = {
    products,
    imageItems,
    getCoverImage,
    loading,
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}
